<template>
  <multiselect
    :id="id"
    class="search-select"
    :class="[size && `search-select--${size}`]"
    :value="currentSelection()"
    :options="listOptions()"
    track-by="key"
    label="text"
    :placeholder="boxPlaceholder()"
    :disabled="disabled"
    :searchable="true"
    :allow-empty="false"
    :show-labels="false"
    :max-height="300"
    open-direction="bottom"
    @input="onInput"
    @open="isOpen = true"
    @close="isOpen = false"
  >
    <template slot="clear">
      <button
        v-if="canClear()"
        type="button"
        class="search-select__clear"
        title="Clear"
        aria-label="Clear"
        @mousedown.prevent.stop="clear"
      >
        &times;
      </button>
    </template>
    <span slot="noResult">No match found</span>
    <span slot="noOptions">No options</span>
  </multiselect>
</template>

<script>
import Multiselect from "vue-multiselect";

// An option whose value is empty and whose label is an instruction ("Pilih
// Site", "Filter Site Group", "-- Sort by --") is a placeholder, not a choice.
// Empty-valued options that are real choices ("Select All", "Global (All
// Roles)", "Tidak Ada") do not match and stay in the list.
const PLACEHOLDER_LABEL =
  /^(pilih\b|filter\b|choose\b|please select\b|select$|select (?!all\b)|--)/i;

// Drop-in, searchable replacement for <b-form-select>.
// v-model holds the raw option value (like b-form-select), not the option
// object vue-multiselect works with. Options can come from the `options`
// prop (primitives or objects, honoring value-field / text-field /
// disabled-field) or from <option> children, including a #first slot.
export default {
  name: "SearchSelect",
  components: { Multiselect },
  model: { prop: "value", event: "input" },
  props: {
    value: { default: null },
    options: { type: [Array, Object], default: () => [] },
    valueField: { type: String, default: "value" },
    textField: { type: String, default: "text" },
    disabledField: { type: String, default: "disabled" },
    placeholder: { type: String, default: "Select" },
    disabled: { type: Boolean, default: false },
    size: { type: String, default: null },
    id: { type: String, default: null },
  },
  data() {
    return { isOpen: false };
  },
  methods: {
    // Methods, not computed: inline <option> children arrive through $slots,
    // which is not reactive, so a computed would cache them forever. The
    // parent re-renders this component whenever its slot content changes.
    currentOptions() {
      return [
        ...this.slotOptions("first"),
        ...this.propOptions(),
        ...this.slotOptions("default"),
      ].map((option, index) => ({
        ...option,
        // vue-multiselect needs a unique, non-null key to track selection;
        // values here can be null, booleans or duplicates
        key: `${index}:${String(option.value)}`,
        isPlaceholder: isPlaceholder(option),
      }));
    },
    listOptions() {
      return this.currentOptions().filter((option) => !option.isPlaceholder);
    },
    placeholderOption() {
      return this.currentOptions().find((option) => option.isPlaceholder);
    },
    currentSelection() {
      // the value sitting on a placeholder reads as "nothing selected", so
      // the box shows the placeholder hint instead of a fake choice
      return (
        this.listOptions().find((option) =>
          sameValue(option.value, this.value)
        ) || null
      );
    },
    boxPlaceholder() {
      const selection = this.currentSelection();
      // while searching, keep showing what is picked instead of a blank hint
      if (this.isOpen && selection) return selection.text;
      const placeholder = this.placeholderOption();
      return placeholder ? placeholder.text : this.placeholder;
    },
    canClear() {
      const placeholder = this.placeholderOption();
      return (
        !this.disabled &&
        Boolean(placeholder) &&
        !sameValue(placeholder.value, this.value)
      );
    },
    clear() {
      const { value } = this.placeholderOption();
      this.$emit("input", value);
      this.$emit("change", value);
    },
    propOptions() {
      const { options } = this;
      if (Array.isArray(options)) {
        return options.map((option) => {
          if (option === null || typeof option !== "object") {
            return { value: option, text: String(option) };
          }
          return {
            value: option[this.valueField],
            text: String(option[this.textField] ?? ""),
            $isDisabled: Boolean(option[this.disabledField]),
          };
        });
      }
      // b-form-select also accepts { value: text }
      return Object.keys(options || {}).map((value) => ({
        value,
        text: String(options[value]),
      }));
    },
    slotOptions(name) {
      const nodes = this.$scopedSlots[name]
        ? this.$scopedSlots[name]() || []
        : this.$slots[name] || [];
      // <optgroup> has no searchable equivalent here, so flatten its options
      const flat = nodes.flatMap((node) =>
        node.tag === "optgroup" ? node.children || [] : [node]
      );
      return flat
        .filter((node) => node.tag === "option")
        .map((node) => {
          const data = node.data || {};
          const domProps = data.domProps || {};
          const attrs = data.attrs || {};
          const value =
            "value" in domProps
              ? domProps.value
              : "value" in attrs
              ? attrs.value
              : textOf(node);
          return {
            value,
            text: textOf(node),
            $isDisabled: "disabled" in attrs && attrs.disabled !== false,
          };
        });
    },
    onInput(option) {
      if (!option) return;
      this.$emit("input", option.value);
      this.$emit("change", option.value);
    },
  },
};

function isPlaceholder({ value, text }) {
  const empty = value === null || value === undefined || value === "";
  return empty && PLACEHOLDER_LABEL.test(text.trim());
}

function textOf(node) {
  return (node.children || [])
    .map((child) => child.text || textOf(child))
    .join("")
    .trim();
}

// b-form-select compares loosely, so "10" matches 10; keep that, but never
// let null/undefined match the string "null"
function sameValue(a, b) {
  if (a === b) return true;
  if (a === null || a === undefined || b === null || b === undefined) {
    return (a ?? null) === (b ?? null);
  }
  if (typeof a === "object" || typeof b === "object") {
    return JSON.stringify(a) === JSON.stringify(b);
  }
  return String(a) === String(b);
}
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
<style lang="scss">
// Match Metronic's .form-control (13px text, 38px tall, #e4e6ef border) so a
// swapped dropdown sits flush with the inputs and datepickers next to it
.search-select.multiselect {
  min-height: calc(1.5em + 1.3rem + 2px);
  font-size: 1rem;
  color: #3f4254;

  .multiselect__tags {
    min-height: calc(1.5em + 1.3rem + 2px);
    padding: 0.65rem 2.75rem 0 1rem;
    border: 1px solid #e4e6ef;
    border-radius: 0.42rem;
    background: #fff;
    font-size: 1rem;
  }

  .multiselect__single,
  .multiselect__input,
  .multiselect__placeholder {
    display: block;
    min-height: 0;
    margin: 0 0 0.65rem;
    padding: 0;
    border: 0;
    font-size: 1rem;
    line-height: 1.5;
    background: transparent;
    color: inherit;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .multiselect__input::placeholder,
  .multiselect__placeholder {
    color: #b5b5c3;
  }

  // Bootstrap's chevron instead of vue-multiselect's solid triangle
  .multiselect__select {
    width: 2.25rem;
    height: 100%;
    padding: 0;

    &::before {
      top: 50%;
      margin-top: -0.2rem;
      border: 0;
      width: 0.6rem;
      height: 0.4rem;
      background: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 8'%3e%3cpath fill='none' stroke='%237e8299' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round' d='M1 1.5l5 5 5-5'/%3e%3c/svg%3e")
        center / contain no-repeat;
      left: 50%;
      margin-left: -0.3rem;
      position: absolute;
      content: "";
    }
  }

  &.multiselect--active .multiselect__select {
    transform: rotate(180deg);
  }

  .search-select__clear {
    position: absolute;
    top: 50%;
    right: 2.1rem;
    z-index: 3;
    width: 1.25rem;
    height: 1.25rem;
    margin-top: -0.625rem;
    padding: 0;
    border: 0;
    border-radius: 50%;
    background: transparent;
    color: #b5b5c3;
    font-size: 1.15rem;
    line-height: 1;
    cursor: pointer;

    &:hover {
      background: #f3f6f9;
      color: #3f4254;
    }
  }

  .multiselect__content-wrapper {
    // native selects widen their list to fit long names; do the same instead
    // of clipping them to the width of a narrow filter column
    min-width: 100%;
    width: max-content;
    max-width: min(32rem, 90vw);
    margin-top: 0.25rem;
    border: 1px solid #e4e6ef;
    border-radius: 0.42rem;
    background: #fff;
    box-shadow: 0 0.5rem 1.5rem rgba(0, 0, 0, 0.08);
    z-index: 1060;
  }

  .multiselect__content {
    padding: 0.35rem 0;
  }

  .multiselect__option {
    min-height: auto;
    padding: 0.55rem 1rem;
    font-size: 1rem;
    line-height: 1.5;
    white-space: normal;
    color: #3f4254;

    &::after {
      display: none;
    }
  }

  .multiselect__option--highlight {
    background: #f3f6f9;
    color: #3699ff;
  }

  .multiselect__option--selected,
  .multiselect__option--selected.multiselect__option--highlight {
    background: #e1f0ff;
    color: #3699ff;
    font-weight: 500;
  }

  .multiselect__option--disabled {
    background: transparent !important;
    color: #b5b5c3 !important;
  }

  &.multiselect--active {
    z-index: 1060;

    .multiselect__tags {
      border-color: #69b3ff;
    }
  }

  // the list sits below the box with a gap, so keep all corners rounded
  &.multiselect--active:not(.multiselect--above) .multiselect__tags,
  &.multiselect--above.multiselect--active .multiselect__tags {
    border-radius: 0.42rem;
  }

  .multiselect__option--group,
  .multiselect__content-wrapper li > span:not(.multiselect__option) {
    padding: 0.55rem 1rem;
    color: #b5b5c3;
  }

  &.multiselect--disabled {
    opacity: 1;
    background: transparent;

    .multiselect__tags,
    .multiselect__select {
      background: #f3f6f9;
    }
  }
}

.search-select--sm.multiselect {
  min-height: calc(1.35em + 1.1rem + 2px);
  font-size: 0.925rem;

  .multiselect__tags {
    min-height: calc(1.35em + 1.1rem + 2px);
    padding: 0.55rem 2.5rem 0 0.75rem;
    border-radius: 0.28rem;
    font-size: 0.925rem;
  }

  .multiselect__single,
  .multiselect__input,
  .multiselect__placeholder {
    margin-bottom: 0.55rem;
    font-size: 0.925rem;
    line-height: 1.35;
  }

  .multiselect__option {
    padding: 0.4rem 0.75rem;
    font-size: 0.925rem;
    line-height: 1.35;
  }

  .search-select__clear {
    right: 1.9rem;
  }
}
</style>
